import { useEffect, useState } from 'react';
import styled from 'styled-components';

import FormTitle from 'components/PageLayout/FormTitle';
import { Button } from 'components/curo-components/common/Button';
import ConfirmationModal from 'components/curo-components/common/ConfirmationModal';
import { GoogleAnalyticsEventType } from 'enums/GoogleAnalyticsEventType';
import { CuroApplicationVariables } from 'enums/curo/Variables';
import { getApplicationSelector } from 'handlers/curo-handlers/selectors';
import { useAppSelector } from 'hooks/reduxHooks';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import useGoogleAnalytics from 'hooks/useGoogleAnalytics';
import media from 'styles/media.module.scss';
import { handleDeclineApplication, updatePageDisplayedTechnical } from 'thunks';

const StyledWrapper = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 36px;

  @media (max-width: ${media.maxTablet}) {
    flex-direction: column;
  }
`;

interface IConnectBankAccountInformationNeededStepProps {
  onStepComplete: () => void;
  onPlaid: () => void;
}

const ConnectBankAccountInformationNeededStep = ({
  onStepComplete,
  onPlaid,
}: IConnectBankAccountInformationNeededStepProps) => {
  const application = useAppSelector(getApplicationSelector)!;
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const trackEvent = useGoogleAnalytics();

  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    trackEvent(GoogleAnalyticsEventType.ApplyProcessMoreInformation);
  }, []);

  const updatePageInteraction = async () => {
    await dispatchWithUnwrap(
      updatePageDisplayedTechnical({
        applicationId: application?.id,
        pageVariableToUpdate:
          CuroApplicationVariables.ConnectBankAccountMoreInformationNeededInteractionTechnical,
      }),
    );
  };

  const handleOpenConfirmModal = () => {
    updatePageInteraction();
    setIsModalOpen(true);
  };

  const handleDeclineBankAccount = async () => {
    if ((application.variables[CuroApplicationVariables.MaxSecured] as number) > 0) {
      onStepComplete();
    } else {
      await dispatchWithUnwrap(handleDeclineApplication());
    }
    setIsModalOpen(false);
  };

  const handleAcceptBankAccount = async () => {
    await updatePageInteraction();
    onPlaid();
    setIsModalOpen(false);
  };

  return (
    <>
      <div>
        <FormTitle
          title="More information needed to find your offer"
          subTitle={
            <div>
              Heights Finance looks at more than a credit score to find you an offer.
              <br />
              <br />
              To provide the best chance of finding you an offer, we need to verify your bank
              account information. Please add the bank account which you deposit your paycheck. This
              process usually can be completed within minutes.
            </div>
          }
        />
        <StyledWrapper>
          <Button onClick={handleAcceptBankAccount} kind="primary" size="form">
            Connect to Primary Bank Account
          </Button>
          <Button onClick={handleOpenConfirmModal} kind="secondary" size="form">
            Not Interested
          </Button>
        </StyledWrapper>
      </div>
      {isModalOpen && (
        <ConfirmationModal
          title="Are you sure?"
          description="Connecting your primary bank account provides us the best chance to find you an offer."
          confirmButtonTitle="Connect my bank account"
          declineButtonTitle="Not Interested"
          handleAccept={handleAcceptBankAccount}
          handleDecline={handleDeclineBankAccount}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </>
  );
};

export default ConnectBankAccountInformationNeededStep;
